<template>
    <ul ref="listComponent">
        <li v-for="account in accountList" :key="account.user_id">
            <section
                class="card"
                :class="openedList.includes(account.user_id) ? 'opened' : 'hidden'"
            >
                <div class="button_cell">
                    <button class="btn_toggle" @click="onExtendClick(account.user_id)">
                        {{ openedList.includes(account.user_id) ? "-" : "+" }}
                    </button>
                </div>

                <ul class="account_information_list">
                    <li
                        v-for="(titleHeader, index) in $tm('tiktok.table.connected_header')"
                        :key="index"
                        class="account_information account_cell"
                        :class="[columnClassList[index]]"
                    >
                        <div
                            v-show="openedList.includes(account.user_id)"
                            class="title"
                            :class="`title-${columnClassList[index]}`"
                        >
                            {{ titleHeader }}
                        </div>

                        <div
                            :class="[
                                `${columnClassList[index]}-value`,
                                statusAccount(account)?.status,
                            ]"
                        >
                            <!-- ACCOUNT -->
                            <template v-if="columnClassList[index] === 'account'">
                                <div class="account-container">
                                    <img class="icon" :src="tiktokIcon"/>
                                    <div class="account_with_tags">
                                        <span
                                            class="username"
                                            :title="account.username"
                                        >{{ account.username }}</span>
                                        <BaseTag :tag="account.tags?.[0]" typeClass="table"/>
                                    </div>
                                </div>
                            </template>

                            <!-- INTEGRATIONS -->
                            <template v-if="columnClassList[index] === 'integration'">
                                <ul
                                    v-if="account.integrations?.length"
                                    class="text-uppercase"
                                    :class="[account.integrations.length > 5 && 'moreCrm']"
                                >
                                    <li
                                        v-for="crm in account.integrations"
                                        :key="crm"
                                        class="headed_text"
                                    >
                                        {{ crm }}
                                    </li>
                                </ul>
                                <p v-else>-</p>
                            </template>

                            <!-- PAID TO -->
                            <template v-if="columnClassList[index] === 'paid_to'">
                                <!-- !TODO: the end date is hidden for now, restore tariff_end_date here -->
                                <span>-</span>
                            </template>

                            <!-- TARIFF -->
                            <template v-if="columnClassList[index] === 'tariff'">
                                <span v-text="account.tariff || '-'"/>
                            </template>

                            <!-- STATUS -->
                            <template v-if="columnClassList[index] === 'status'">
                                <div
                                    v-if="!openedList.includes(account.user_id) && width <= 950"
                                    class="status icon_status mt-1"
                                    :class="statusAccount(account)?.status"
                                >
                                    <i
                                        class="bi bi-circle-fill"
                                        :title="statusLabel(statusAccount(account)?.status)"
                                    />
                                </div>
                                <slot v-else name="status" :account="account"/>
                            </template>

                            <!-- API KEY -->
                            <template v-if="columnClassList[index] === 'api_key'">
                                <div class="api_key">{{ account.api_key }}</div>
                            </template>

                            <!-- SETTINGS -->
                            <template v-if="columnClassList[index] === 'settings'">
                                <slot name="settings" :account="account"/>
                            </template>

                            <!-- ACTIONS -->
                            <template v-if="columnClassList[index] === 'actions'">
                                <slot name="actions" :account="account"/>
                            </template>
                        </div>
                    </li>
                </ul>
            </section>
        </li>
    </ul>

    <div v-if="tableStatus">
        <InfoDataTextRecurring :value="tableStatus" height="120px"/>
    </div>

    <slot name="pagination"/>
</template>

<script setup>
// core
import {ref, reactive, onMounted, onUpdated, nextTick} from "vue";
import {useWindowSize} from "@vueuse/core";

// components
import BaseTag from "@/components/ui/BaseTag.vue";
import InfoDataTextRecurring from "@/components/Recurring/InfoDataTextRecurring.vue";

// configs
import statusAccountConfig from "../configs/statusAccountConfig";
import useAccountStatusLabel from "../configs/useAccountStatusLabel";

import tiktokIcon from "@/assets/icons/tiktok.svg";

const columnClassList = [
    "account",
    "integration",
    "paid_to",
    "tariff",
    "status",
    "api_key",
    "settings",
    "actions",
];

const {width} = useWindowSize();
const {statusLabel} = useAccountStatusLabel();
const openedList = reactive([]);

defineProps({
    accountList: {
        type: Array,
        default: () => [],
    },
    tableType: {
        type: String, // 'visible' || 'invisible'
        default: "visible",
    },
    // 'loading' | 'noAccounts' | null - null means the list has rows
    tableStatus: {
        type: String,
        default: null,
    },
});

defineEmits(["loadMore"]);

const onExtendClick = (userId) => {
    if (openedList.includes(userId)) {
        openedList.splice(openedList.indexOf(userId), 1);
    } else {
        openedList.push(userId);
    }
};

const statusAccount = (account) => statusAccountConfig(account);

// the fade only makes sense on the usernames that really got cut off
const listComponent = ref(null);

const markClippedUsernames = async () => {
    await nextTick();

    listComponent.value?.querySelectorAll(".username").forEach((el) => {
        el.classList.toggle("is-clipped", el.scrollWidth > el.clientWidth + 1);
    });
};

onMounted(markClippedUsernames);
onUpdated(markClippedUsernames);
</script>

<style lang="scss" scoped>
@use "@/styles/table.scss";

.card {
    overflow: hidden;
    background-color: var(--block-color);
    border-radius: 11px;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 5px;
    text-align: center;
    transition: max-height 0.7s ease;

    .title {
        font-weight: 600;
        font-size: 14px;
        text-transform: uppercase;
        margin-bottom: 5px;
    }
}

.moreCrm {
    height: 120px;
    overflow-y: auto;
    margin-bottom: 5px;
    margin-right: 5px;

    &::-webkit-scrollbar {
        width: 3px;
    }

    &::-webkit-scrollbar-track {
        background: white;
    }

    &::-webkit-scrollbar-thumb {
        background: #7970dfbf;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: var(--general-color);
    }
}

.hidden {
    .integration,
    .tariff,
    .status,
    .settings,
    .paid_to,
    .actions,
    .api_key {
        display: none;
    }

    .account_information {
        margin: auto 0;
    }
}

.account_information_list {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    justify-content: space-between;
    gap: 10px;
    flex: 1;
    padding: 5px 10px 0 0;
}

.button_cell {
    margin: 10px;
}

.btn_toggle {
    font-size: 20px;
    color: #7a70df;
    width: 27px;
    padding: 0;
    height: 27px;
    background: white;
    border-radius: 7px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.account_cell {
    border-bottom: none;

    &.account {
        margin: 0;
    }

    &.status,
    &.actions,
    &.settings {
        padding-bottom: 6px;
    }

    &.settings {
        flex: 0 0 100%;
    }

    &.paid_to {
        width: 90px;
    }

    &.actions {
        max-width: 190px;
        width: 100%;
    }

    .account-value {
        display: flex;
        width: 100%;
    }

    .actions-value {
        text-align: right;
    }

    .settings-value {
        width: fit-content;
    }
}

.opened {
    max-height: 800px;

    .account_information_list {
        padding-top: 10px;
    }

    .account_cell {
        border-bottom: 1px solid rgba(122, 112, 223, 0.15);
        display: block;
        flex: 100%;
        text-align: left;

        &.settings {
            max-width: 100%;
        }
    }
}

.status-value {
    font-size: 15px;
    transition: none;
    min-width: 190px;
}

.account-container {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 145px;
    flex: 1;
}

.account_with_tags {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

// a long username scrolls inside its slot instead of stretching the card,
// the same way the SIP password does
.username {
    max-width: 100%;
    overflow-x: auto;
    white-space: nowrap;

    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }

    &.is-clipped {
        -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 20px), transparent 100%);
        mask-image: linear-gradient(to right, #000 calc(100% - 20px), transparent 100%);
    }
}

.icon {
    margin-top: 4px;
}

.icon_status {
    width: fit-content;
}

::-webkit-scrollbar {
    width: 6px;
}

@media screen and (min-width: 420px) {
    .hidden {
        .status {
            display: block;

            @media (max-width: 950px) {
                margin-right: auto;
            }
        }
    }
}

@media (max-width: 950px) {
    .status-value {
        min-width: auto;
    }

    .hidden .status-value {
        padding-left: 15px;
    }
}

@media screen and (min-width: 560px) {
    .opened .account_cell {
        flex: 1 1 40%;
        min-height: 94px;

        .title-settings,
        .title-actions {
            visibility: hidden;
        }

        .api_key-value {
            margin-top: 10px;
        }
    }

    .settings-value {
        margin-left: auto;
    }
}

@media screen and (min-width: 620px) {
    .hidden .actions {
        display: block;
    }
}

@media screen and (min-width: 800px) {
    .opened {
        .account_cell {
            flex: 1 1 25%;
        }
    }

    .hidden {
        .account_cell {
            flex: 0 0 auto;

            &.settings {
                flex: none;
            }
        }

        .settings {
            display: block;
        }
    }

    .actions {
        width: 100%;
    }
}

@media screen and (min-width: 880px) {
    .hidden {
        .paid_to {
            display: block;
            flex: 0.2 0 auto;
        }

        .actions {
            flex: 3;
        }

        .settings-value {
            margin: auto;
        }
    }

    .icon_status {
        display: block;
    }
}

.integration-value {
    container-type: inline-size;
}

@container (min-width: 191px) {
    .integration-value {
        .moreCrm {
            display: flex;
            column-gap: 5px;
            flex-wrap: wrap;
            overflow: hidden;
            height: auto;
            line-height: 1.2rem;
        }

        .headed_text:not(:last-child):after {
            content: ", ";
        }
    }
}
</style>
